<script>
import { mapState } from "vuex";
import { api } from "@/state/services";
import { canAccessRegionalMenu } from "@/utils/regional-user-permissions";
import { chaveUsuario, isSecretaria, mesAno, nomeMes } from "@/utils/minhas-metas";
import AvisoTruck from "@/components/metas/aviso-truck";
import { isMenuItemVisible } from "@/utils/menu-map";
import { IMG_BRASAO, IMG_QUALIFICACAO, IMG_PIN, IMG_FORMADOR, ROTULO_FAIXA } from "@/utils/jornada";

// Cache do GET metas/conquistas por sessão (o home é remontado a cada visita). O servidor também
// cacheia (qualificação 10 min, PIN 1 h) — aqui é só para não repetir a chamada ao voltar ao home.
const CACHE_MS = 5 * 60 * 1000;
let cacheSessao = null;

const NOME_PRODUTO = { brasil: "APVS Brasil", truck: "APVS Truck" };

/**
 * "Minha Jornada" — bloco do dashboard (logo abaixo do Banner).
 *   1ª faixa: brasão de carreira (permanente). Atual colorido; anteriores cinza com check;
 *             posteriores cinza com cadeado.
 *   2ª faixa: qualificação do mês atual (Brasil e Truck separados).
 *   3ª faixa: qualificação do mês seguinte (projeção).
 *   4ª faixa: PIN da Placa — maior base ativa já atingida (Brasil e Truck separados).
 * Toda a regra vem pronta do GET metas/conquistas (calculado a partir do token).
 *
 * SECRETÁRIA: o endpoint usa a mesma permissão de "/minhas-metas". Sem ela o bloco nem chama a API
 * (um 403 derrubaria a sessão pelo interceptor) e simplesmente não aparece.
 * Qualquer erro => o bloco some em silêncio (o dashboard continua funcionando).
 */
export default {
  name: "MinhaJornada",
  components: { AvisoTruck },
  data() {
    return {
      dados: null,
      carregando: false,
      falhou: false,
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    user() {
      return (this.account && this.account.user) || null;
    },
    permitido() {
      if (!this.user) return false;
      return !isSecretaria(this.user) || canAccessRegionalMenu(this.user, "/minhas-metas");
    },
    visivel() {
      return this.permitido && !this.falhou && (this.carregando || !!this.dados);
    },
    carreira() {
      return this.dados ? this.dados.carreira : null;
    },
    // Jornada do Formador (NULL = tabela ainda não aplicada no banco => faixa escondida).
    formacao() {
      const f = this.dados && this.dados.formacao;
      return f && Array.isArray(f.niveis) ? f : null;
    },
    mostrarCartao() {
      return isMenuItemVisible(this.user, "meu-cartao");
    },
    produtos() {
      if (!this.dados) return [];
      return ["brasil", "truck"].filter((p) => this.dados.produtos && this.dados.produtos[p]);
    },
    qualifAtual() {
      return this.linhasQualif("mes_atual");
    },
    qualifSeguinte() {
      return this.linhasQualif("mes_seguinte");
    },
    rotuloMesAtual() {
      const l = this.qualifAtual[0];
      return l ? mesAno(l.q.mes, l.q.ano) : "";
    },
    rotuloMesSeguinte() {
      const l = this.qualifSeguinte[0];
      return l ? mesAno(l.q.mes, l.q.ano) : "";
    },
    blocosQualif() {
      return [
        { chave: "atual", titulo: "Qualificação de " + this.rotuloMesAtual, linhas: this.qualifAtual },
        { chave: "seguinte", titulo: "Qualificação de " + this.rotuloMesSeguinte, linhas: this.qualifSeguinte },
      ].filter((b) => b.linhas.length);
    },
    // PIN único: a base ativa Brasil + Truck é somada no servidor (mesmo mês).
    pins() {
      if (!this.dados || !this.dados.pins || !this.dados.pins.niveis) return [];
      return [{ produto: "total", nome: "Base ativa total", d: this.dados.pins }];
    },
  },
  created() {
    this.carregar();
  },
  methods: {
    nomeMes,
    imgBrasao(key) {
      return IMG_BRASAO[key] || IMG_BRASAO.consultor;
    },
    imgQualificacao(pct) {
      return IMG_QUALIFICACAO[pct] || null;
    },
    // Sempre os 3 brasões (5%, 7%, 10%): colorido se atingiu, cinza se não. A meta mínima de
    // cada faixa vem da API (q.faixas) quando disponível; sem ela, acende pelo percentual atingido.
    // Igual à carreira: faixa atual colorida; faixas abaixo dela (já passou) cinza com check;
    // faixas não atingidas cinza com cadeado.
    statusFaixa(f, q) {
      if (!f.atingida) return "bloqueado";
      return f.percentual === q.percentual ? "atual" : "conquistado";
    },
    faixasDe(q) {
      const daApi = {};
      (q.faixas || []).forEach((f) => {
        daApi[f.percentual] = f;
      });
      const pctAtingido = q.qualificado && q.percentual ? q.percentual : 0;
      // Regional Home Office só tem 5% e 7% (a API manda as faixas da modalidade); sem a API
      // nova, mostra as 3.
      const lista = q.faixas && q.faixas.length > 1 ? q.faixas.map((f) => f.percentual) : [5, 7, 10];
      return lista.map((pct) => {
        const f = daApi[pct];
        return {
          percentual: pct,
          minimo: f ? f.minimo : null,
          atingida: f ? !!f.atingida : pct <= pctAtingido,
        };
      });
    },
    imgPin(nivel) {
      return IMG_PIN[nivel] || null;
    },
    fmtNum(v) {
      return Number(v || 0).toLocaleString("pt-BR");
    },
    carregar() {
      if (!this.permitido) return;
      const key = chaveUsuario(this.user);
      if (cacheSessao && cacheSessao.key === key && Date.now() - cacheSessao.em < CACHE_MS) {
        this.dados = cacheSessao.data;
        return;
      }
      this.carregando = true;
      api
        .get("metas/conquistas")
        .then((response) => {
          const d = response && response.data;
          if (!d || d.status !== "success") {
            this.falhou = true;
            return;
          }
          this.dados = d;
          cacheSessao = { key, em: Date.now(), data: d };
        })
        .catch(() => {
          this.falhou = true;
        })
        .finally(() => {
          this.carregando = false;
        });
    },
    linhasQualif(campo) {
      if (!this.dados || !this.dados.qualificacao) return [];
      return this.produtos
        .filter((p) => this.dados.qualificacao[p] && this.dados.qualificacao[p][campo])
        .map((p) => ({ produto: p, nome: NOME_PRODUTO[p], q: this.dados.qualificacao[p][campo] }));
    },
    rotuloFaixa(q) {
      if (q.status === "aguardando") return "Em breve";
      if (q.status === "sem_dados") return "Sem dados";
      if (q.faixa === "cinza") return ROTULO_FAIXA.cinza;
      return ROTULO_FAIXA[q.faixa] + (q.percentual ? " " + q.percentual + "%" : "");
    },
    textoQualif(q) {
      if (q.status === "aguardando") return "Começa a contar depois do corte do dia 5.";
      if (q.status === "sem_dados") return "O fechamento deste mês ainda não foi processado.";
      const base = q.contando + " de " + q.meta + (q.projecao ? " contando até agora" : " no fechamento");
      if (q.proxima) {
        const alvo = ROTULO_FAIXA[q.proxima.faixa] + (q.proxima.percentual ? " " + q.proxima.percentual + "%" : "");
        return base + " · " + (q.projecao ? "faltam " : "faltaram ") + q.proxima.faltam + " para " + alvo;
      }
      return base + " · faixa máxima!";
    },
    tooltipQualif(q, nome) {
      if (q.status === "aguardando") {
        return nome + ": este fechamento só começa a contar depois do corte do dia 5 do mês atual.";
      }
      if (q.status === "sem_dados") return nome + ": ainda sem arquivo de metas processado para este mês.";
      let t = nome + ": " + (q.qualificado ? "qualificado(a) em " + this.rotuloFaixa(q) + "." : "ainda sem qualificação.");
      t += " Metas contando: " + q.contando + " (meta " + q.meta + ").";
      if (q.proxima) t += " Faltam " + q.proxima.faltam + " para " + ROTULO_FAIXA[q.proxima.faixa] + ".";
      if (q.projecao) t += " Projeção com o que já está contando — o resultado oficial sai no corte do dia 5.";
      return t;
    },
    tooltipBrasao(n) {
      if (n.status === "atual") return "Sua carreira atual: " + n.label + ".";
      if (n.status === "conquistado") return n.label + ": etapa já conquistada.";
      return n.label + ": próxima(s) etapa(s) da carreira — ainda bloqueada.";
    },
    mesCurto(mes, ano) {
      return nomeMes(mes).slice(0, 3).toLowerCase() + "/" + ano;
    },
    // Base ativa atual (Brasil + Truck, último mês processado). API antiga sem "atual" => usa a histórica.
    baseAtual(p) {
      return p.d.atual || { base: p.d.max_base, brasil: 0, truck: 0, mes: null, ano: null };
    },
    pctProximo(p) {
      if (!p.d.proximo) return 100;
      return Math.min(100, Math.round((this.baseAtual(p).base / p.d.proximo.nivel) * 100));
    },
    // Igual à carreira: maior PIN conquistado colorido; os abaixo dele cinza com check; os demais
    // cinza com cadeado (o próximo um pouco mais visível).
    statusPin(p, nivel) {
      return this.statusNivel(p.d.max_base, p.d.niveis, p.d.proximo, nivel);
    },
    // Regra comum a PIN e Formador: maior nível atingido = "atual"; abaixo = "conquistado";
    // o próximo = "proximo"; demais = "bloqueado".
    statusNivel(valor, niveis, proximo, nivel) {
      const conquistados = niveis.filter((n) => valor >= n);
      const maior = conquistados.length ? conquistados[conquistados.length - 1] : null;
      if (nivel === maior) return "atual";
      if (valor >= nivel) return "conquistado";
      return proximo && proximo.nivel === nivel ? "proximo" : "bloqueado";
    },
    imgFormador(nivel) {
      return IMG_FORMADOR[nivel] || null;
    },
    tooltipFormador(nivel) {
      const f = this.formacao;
      if (f.total >= nivel) return "Formador " + nivel + " conquistado!";
      const falta = nivel - f.total;
      return "Formador " + nivel + ": faltam " + falta + (falta === 1 ? " líder formado." : " líderes formados.");
    },
    titulo(texto) {
      return String(texto || "")
        .toLowerCase()
        .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
    },
    tooltipPin(p, nivel) {
      if (p.d.max_base >= nivel) return "PIN " + this.fmtNum(nivel) + " conquistado — garantido pela sua base histórica.";
      const falta = Math.max(0, nivel - this.baseAtual(p).base);
      return "PIN " + this.fmtNum(nivel) + ": faltam " + this.fmtNum(falta) + " placas na base ativa atual.";
    },
  },
};
</script>

<template>
  <section v-if="visivel" class="card mj" aria-labelledby="mj-titulo">
    <div class="card-body">
      <div class="mj-head">
        <div>
          <h2 id="mj-titulo" class="mj-h2"><i class="bx bx-trophy" aria-hidden="true"></i> Minha Jornada</h2>
          <p class="mj-muted mb-0">Sua carreira, qualificações e conquistas de placas.</p>
        </div>
        <div class="d-flex flex-wrap" style="gap: 0.5rem">
          <router-link v-if="mostrarCartao" to="/meu-cartao" class="btn btn-light btn-sm">
            <i class="bx bx-id-card" aria-hidden="true"></i> Ver meu cartão
          </router-link>
          <router-link v-if="produtos.length" to="/minhas-metas" class="btn btn-light btn-sm">
            Minhas Metas <i class="bx bx-chevron-right" aria-hidden="true"></i>
          </router-link>
        </div>
      </div>

      <!-- Carregando -->
      <div v-if="carregando && !dados" class="mj-skeleton" aria-busy="true" aria-label="Carregando Minha Jornada">
        <div class="mj-sk-linha">
          <span v-for="n in 7" :key="n" class="mj-sk-circulo"></span>
        </div>
        <div class="mj-sk-grade">
          <span class="mj-sk-bloco"></span>
          <span class="mj-sk-bloco"></span>
        </div>
      </div>

      <template v-else-if="dados">
        <!-- 1ª faixa: brasão de carreira -->
        <div v-if="carreira" class="mj-faixa">
          <h3 class="mj-h3">Carreira · <span class="mj-destaque">{{ carreira.atual.label }}</span></h3>
          <ol class="mj-brasoes" aria-label="Níveis da carreira">
            <li
              v-for="n in carreira.niveis"
              :key="n.key"
              v-b-tooltip.hover.focus.bottom
              class="mj-brasao"
              :class="'mj-' + n.status"
              :title="tooltipBrasao(n)"
              tabindex="0"
              :aria-current="n.status === 'atual' ? 'step' : null"
            >
              <span class="mj-brasao-img">
                <img :src="imgBrasao(n.key)" :alt="n.label" loading="lazy" width="96" height="96" />
                <i v-if="n.status === 'conquistado'" class="bx bx-check mj-selo-ok" aria-hidden="true"></i>
                <i v-else-if="n.status === 'bloqueado'" class="bx bx-lock-alt mj-selo-lock" aria-hidden="true"></i>
              </span>
              <span class="mj-brasao-label">{{ n.label }}</span>
              <span class="sr-only">
                {{ n.status === "atual" ? "(sua carreira atual)" : n.status === "conquistado" ? "(conquistado)" : "(bloqueado)" }}
              </span>
            </li>
          </ol>
        </div>

        <!-- 2ª e 3ª faixas: qualificação -->
        <div v-for="bloco in blocosQualif" :key="bloco.chave" class="mj-faixa">
          <h3 class="mj-h3">
            {{ bloco.titulo }}
            <span v-if="bloco.linhas[0].q.projecao && bloco.linhas[0].q.status === 'em_andamento'" class="mj-tag">em andamento</span>
          </h3>
          <div class="mj-grade">
            <div
              v-for="l in bloco.linhas"
              :key="l.produto"
              v-b-tooltip.hover.focus.bottom
              class="mj-qualif"
              :class="'mj-q-' + l.q.faixa"
              :title="tooltipQualif(l.q, l.nome)"
              tabindex="0"
            >
              <div class="mj-qualif-topo">
                <strong>{{ l.nome }}</strong>
                <span class="mj-q-rotulo">{{ rotuloFaixa(l.q) }}</span>
              </div>
              <ol class="mj-escudos" :aria-label="'Faixas de qualificação ' + l.nome">
                <li
                  v-for="f in faixasDe(l.q)"
                  :key="f.percentual"
                  class="mj-escudo-item"
                  :class="'mj-' + statusFaixa(f, l.q)"
                >
                  <span class="mj-brasao-img">
                    <img
                      :src="imgQualificacao(f.percentual)"
                      :alt="'Faixa ' + f.percentual + '%'"
                      loading="lazy"
                      width="72"
                      height="72"
                    />
                    <i v-if="statusFaixa(f, l.q) === 'conquistado'" class="bx bx-check mj-selo-ok" aria-hidden="true"></i>
                    <i v-else-if="statusFaixa(f, l.q) === 'bloqueado'" class="bx bx-lock-alt mj-selo-lock" aria-hidden="true"></i>
                  </span>
                  <span class="mj-escudo-legenda">{{ f.minimo ? f.minimo + "+ · " : "" }}{{ f.percentual }}%</span>
                  <span class="sr-only">{{ f.atingida ? "(atingida)" : "(não atingida)" }}</span>
                </li>
              </ol>
              <span class="mj-muted">{{ textoQualif(l.q) }}</span>
            </div>
          </div>
        </div>

        <!-- 4ª faixa: PIN da Placa -->
        <div v-if="pins.length" class="mj-faixa">
          <h3 class="mj-h3">PIN da Placa <span class="mj-tag">Brasil + Truck</span></h3>
          <div v-for="p in pins" :key="p.produto" class="mj-pins">
            <ol class="mj-pins-lista" aria-label="PINs da Placa">
              <li
                v-for="nivel in p.d.niveis"
                :key="nivel"
                v-b-tooltip.hover.focus.bottom
                class="mj-pin"
                :class="'mj-' + statusPin(p, nivel)"
                :title="tooltipPin(p, nivel)"
                tabindex="0"
              >
                <span class="mj-brasao-img">
                  <img :src="imgPin(nivel)" :alt="'PIN ' + nivel" loading="lazy" width="96" height="96" />
                  <i v-if="statusPin(p, nivel) === 'conquistado'" class="bx bx-check mj-selo-ok" aria-hidden="true"></i>
                  <i v-else-if="statusPin(p, nivel) !== 'atual'" class="bx bx-lock-alt mj-selo-lock" aria-hidden="true"></i>
                </span>
                <span class="mj-pin-legenda">{{ fmtNum(nivel) }}</span>
                <span class="sr-only">({{ statusPin(p, nivel) }})</span>
              </li>
            </ol>

            <div class="mj-bases">
              <div class="mj-base mj-base-hist">
                <span class="mj-base-rotulo"><i class="bx bx-shield-quarter" aria-hidden="true"></i> Base histórica</span>
                <strong class="mj-base-num">{{ fmtNum(p.d.max_base) }} <small>placas</small></strong>
                <span class="mj-muted">
                  {{ p.d.mes ? "Maior já atingida (" + mesCurto(p.d.mes, p.d.ano) + ")" : "Ainda sem base ativa registrada" }}
                  <template v-if="p.d.quebra && p.d.quebra.truck > 0">
                    · Brasil {{ fmtNum(p.d.quebra.brasil) }} + Truck {{ fmtNum(p.d.quebra.truck) }}
                  </template>
                </span>
              </div>
              <div class="mj-base mj-base-atual">
                <span class="mj-base-rotulo"><i class="bx bx-car" aria-hidden="true"></i> Base ativa atual</span>
                <strong class="mj-base-num">{{ fmtNum(baseAtual(p).base) }} <small>placas</small></strong>
                <span class="mj-muted">
                  {{ baseAtual(p).mes ? "Último mês processado (" + mesCurto(baseAtual(p).mes, baseAtual(p).ano) + ")" : "Base de hoje" }}
                  <template v-if="baseAtual(p).truck > 0">
                    · Brasil {{ fmtNum(baseAtual(p).brasil) }} + Truck {{ fmtNum(baseAtual(p).truck) }}
                  </template>
                </span>
              </div>
            </div>

            <div v-if="p.d.proximo" class="mj-pin-progresso">
              <div class="mj-pin-progresso-topo">
                <span>
                  Faltam <strong>{{ fmtNum(p.d.proximo.faltam) }} placas</strong> na base ativa atual para o
                  <strong>PIN {{ fmtNum(p.d.proximo.nivel) }}</strong>
                </span>
                <span class="mj-muted">{{ fmtNum(baseAtual(p).base) }} de {{ fmtNum(p.d.proximo.nivel) }}</span>
              </div>
              <div
                class="mj-barra"
                role="progressbar"
                :aria-valuenow="baseAtual(p).base"
                aria-valuemin="0"
                :aria-valuemax="p.d.proximo.nivel"
              >
                <span :style="{ width: pctProximo(p) + '%' }"></span>
              </div>
            </div>
            <p v-else class="mj-pin-max mb-0">
              <i class="bx bx-crown" aria-hidden="true"></i> Todos os PINs conquistados!
            </p>

            <p class="mj-pin-nota mb-0">
              <i class="bx bx-info-circle" aria-hidden="true"></i>
              Uma vez atingido, o PIN é seu: a <strong>base histórica</strong> mantém você nele, mesmo que a base
              atual caia. Para o próximo PIN, o que conta é a <strong>base ativa atual</strong>.
            </p>
          </div>
        </div>

        <!-- 5ª faixa: Formação de Líderes (mesmo padrão do PIN da Placa) -->
        <div v-if="formacao" class="mj-faixa">
          <h3 class="mj-h3">Formação de Líderes</h3>
          <div class="mj-pins">
            <ol class="mj-pins-lista" aria-label="Brasões da Formação de Líderes">
              <li
                v-for="nivel in formacao.niveis"
                :key="nivel"
                v-b-tooltip.hover.focus.bottom
                class="mj-pin"
                :class="'mj-' + statusNivel(formacao.total, formacao.niveis, formacao.proximo, nivel)"
                :title="tooltipFormador(nivel)"
                tabindex="0"
              >
                <span class="mj-brasao-img">
                  <img :src="imgFormador(nivel)" :alt="'Formador ' + nivel" loading="lazy" width="96" height="96" />
                  <i
                    v-if="statusNivel(formacao.total, formacao.niveis, formacao.proximo, nivel) === 'conquistado'"
                    class="bx bx-check mj-selo-ok"
                    aria-hidden="true"
                  ></i>
                  <i
                    v-else-if="statusNivel(formacao.total, formacao.niveis, formacao.proximo, nivel) !== 'atual'"
                    class="bx bx-lock-alt mj-selo-lock"
                    aria-hidden="true"
                  ></i>
                </span>
                <span class="mj-pin-legenda">{{ nivel }} líderes</span>
                <span class="sr-only">({{ statusNivel(formacao.total, formacao.niveis, formacao.proximo, nivel) }})</span>
              </li>
            </ol>

            <div class="mj-bases">
              <div class="mj-base mj-base-hist">
                <span class="mj-base-rotulo"><i class="bx bx-group" aria-hidden="true"></i> Líderes formados</span>
                <strong class="mj-base-num">{{ fmtNum(formacao.total) }} <small>{{ formacao.total === 1 ? "líder" : "líderes" }}</small></strong>
                <span class="mj-muted">
                  <template v-if="formacao.pela_regional">{{ fmtNum(formacao.pela_regional) }} na sua regional</template>
                  <template v-if="formacao.pela_regional && formacao.por_indicacao"> · </template>
                  <template v-if="formacao.por_indicacao">{{ fmtNum(formacao.por_indicacao) }} por indicação direta</template>
                  <template v-if="!formacao.total">Nenhum líder formado ainda</template>
                </span>
              </div>
              <div class="mj-base mj-base-atual">
                <span class="mj-base-rotulo"><i class="bx bx-star" aria-hidden="true"></i> Último líder formado</span>
                <template v-if="formacao.ultimo">
                  <strong class="mj-base-nome">{{ titulo(formacao.ultimo.nome) || "—" }}</strong>
                  <span class="mj-muted">{{ mesCurto(formacao.ultimo.mes, formacao.ultimo.ano) }}</span>
                </template>
                <span v-else class="mj-muted">
                  Um consultor vira Líder ao chegar a 30 placas ativas. Ajude sua equipe a chegar lá!
                </span>
              </div>
            </div>

            <div v-if="formacao.proximo" class="mj-pin-progresso">
              <div class="mj-pin-progresso-topo">
                <span>
                  Faltam <strong>{{ fmtNum(formacao.proximo.faltam) }} {{ formacao.proximo.faltam === 1 ? "líder" : "líderes" }}</strong>
                  para o brasão <strong>Formador {{ formacao.proximo.nivel }}</strong>
                </span>
                <span class="mj-muted">{{ fmtNum(formacao.total) }} de {{ fmtNum(formacao.proximo.nivel) }}</span>
              </div>
              <div
                class="mj-barra"
                role="progressbar"
                :aria-valuenow="formacao.total"
                aria-valuemin="0"
                :aria-valuemax="formacao.proximo.nivel"
              >
                <span :style="{ width: Math.min(100, Math.round((formacao.total / formacao.proximo.nivel) * 100)) + '%' }"></span>
              </div>
            </div>
            <p v-else class="mj-pin-max mb-0">
              <i class="bx bx-crown" aria-hidden="true"></i> Todos os brasões de Formador conquistados!
            </p>

            <p class="mj-pin-nota mb-0">
              <i class="bx bx-info-circle" aria-hidden="true"></i>
              Conta cada consultor que virou Líder (30 placas ativas) na sua regional ou por sua indicação direta.
              É um histórico: o líder continua contando mesmo que depois vire regional ou mude de equipe.
            </p>
          </div>
        </div>

        <AvisoTruck v-if="produtos.includes('truck')" compacto class="mb-0" />
      </template>
    </div>
  </section>
</template>

<style scoped>
.mj {
  border-radius: var(--st-radius-lg);
}
.mj-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.mj-h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--st-text);
}
.mj-h2 i {
  color: var(--st-warning-strong);
}
.mj-h3 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--st-text-muted);
}
.mj-destaque {
  color: var(--st-brand);
}
.mj-tag {
  padding: 0.0625rem 0.5rem;
  border-radius: 999px;
  background: var(--st-brand-soft);
  color: var(--st-brand);
  font-size: 0.6875rem;
  letter-spacing: 0;
  text-transform: none;
}
.mj-muted {
  display: block;
  color: var(--st-text-muted);
  font-size: 0.8125rem;
}
.mj-faixa {
  padding: 0.875rem 0;
  border-top: 1px solid var(--st-border-soft);
}

/* Brasões */
.mj-brasoes {
  display: flex;
  gap: 0.5rem;
  margin: 0;
  padding: 0.25rem 0 0.25rem;
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}
.mj-brasao {
  display: flex;
  flex: 0 0 5.5rem;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.25rem;
  border-radius: var(--st-radius-lg);
  scroll-snap-align: center;
  text-align: center;
  outline: none;
}
.mj-brasao:focus-visible {
  box-shadow: 0 0 0 3px var(--st-focus-ring);
}
.mj-brasao-img {
  position: relative;
  display: inline-block;
}
.mj-brasao img {
  width: 3.5rem;
  height: 3.5rem;
  object-fit: contain;
  transition: transform 0.3s ease;
}
.mj-brasao-label {
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.2;
  color: var(--st-text-muted);
}
.mj-atual {
  flex-basis: 7rem;
  background: var(--st-brand-soft);
}
.mj-atual img {
  width: 5rem;
  height: 5rem;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25));
  animation: mj-brilho 3s ease-in-out infinite;
}
.mj-atual .mj-brasao-label {
  font-size: 0.8125rem;
  font-weight: 800;
  color: var(--st-brand);
}
.mj-conquistado img {
  filter: grayscale(1);
  opacity: 0.7;
}
.mj-bloqueado img {
  filter: grayscale(1);
  opacity: 0.3;
}
.mj-selo-ok,
.mj-selo-lock {
  position: absolute;
  right: -0.25rem;
  bottom: -0.125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  font-size: 0.875rem;
  color: var(--st-text-inverse);
}
.mj-selo-ok {
  background: var(--st-success);
}
.mj-selo-lock {
  background: var(--st-text-soft);
}

/* Qualificação */
.mj-grade {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.mj-qualif {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
  background: var(--st-surface);
  outline: none;
}
.mj-qualif:focus-visible,
.mj-pin:focus-visible {
  box-shadow: 0 0 0 3px var(--st-focus-ring);
}
.mj-qualif-topo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem 0.5rem;
}
.mj-qualif-topo strong {
  font-size: 0.875rem;
  color: var(--st-text);
}
.mj-q-rotulo {
  padding: 0.0625rem 0.5rem;
  border-radius: 999px;
  background: var(--st-canvas);
  color: var(--st-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}
.mj-q-bronze .mj-q-rotulo { background: #fce8d5; color: #7d4e1e; }
.mj-q-prata .mj-q-rotulo { background: #eceff3; color: #4a5361; }
.mj-q-ouro .mj-q-rotulo { background: #fff3c4; color: #7a5a00; }
.mj-escudos {
  display: flex;
  justify-content: space-around;
  gap: 0.5rem;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
}
.mj-escudo-item {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.5rem;
  border-radius: var(--st-radius-lg);
}
.mj-escudo-item img {
  width: 4rem;
  height: 4rem;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.35;
  transition: transform 0.3s ease;
}
.mj-escudo-item.mj-conquistado img {
  opacity: 0.7;
}
.mj-escudo-item.mj-atual img {
  filter: none;
  opacity: 1;
}
.mj-escudo-item.mj-atual img {
  width: 4.75rem;
  height: 4.75rem;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25));
  animation: mj-brilho 3s ease-in-out infinite;
}
.mj-escudo-legenda {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--st-text-soft);
}
.mj-escudo-item.mj-atual .mj-escudo-legenda {
  color: var(--st-text);
}

/* PINs */
.mj-pins {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 0.75rem;
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius-lg);
}
.mj-pins-lista {
  display: flex;
  gap: 0.5rem;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}
.mj-pin {
  display: flex;
  flex: 1 0 5rem;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.25rem;
  border-radius: var(--st-radius-lg);
  scroll-snap-align: center;
  outline: none;
}
.mj-pin img {
  width: 4.5rem;
  height: 4.5rem;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.3;
}
.mj-pin.mj-conquistado img {
  opacity: 0.7;
}
.mj-pin.mj-proximo img {
  opacity: 0.55;
}
.mj-pin.mj-atual img {
  width: 6rem;
  height: 6rem;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25));
  opacity: 1;
  animation: mj-brilho 3s ease-in-out infinite;
}
.mj-pin-legenda {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--st-text-soft);
}
.mj-pin.mj-atual .mj-pin-legenda {
  color: var(--st-brand);
}

/* Base histórica x atual */
.mj-bases {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.mj-base {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.625rem 0.75rem;
  border-left: 4px solid var(--st-border-soft);
  border-radius: var(--st-radius);
  background: var(--st-canvas);
}
.mj-base-hist {
  border-left-color: var(--st-warning);
}
.mj-base-atual {
  border-left-color: var(--st-brand);
}
.mj-base-rotulo {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--st-text-muted);
}
.mj-base-num {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--st-text);
}
.mj-base-num small {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--st-text-muted);
}
.mj-base-nome {
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.3;
  color: var(--st-text);
}
.mj-pin-progresso-topo {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.25rem 0.75rem;
  margin-bottom: 0.375rem;
  font-size: 0.875rem;
  color: var(--st-text);
}
.mj-pin-progresso-topo .mj-muted {
  display: inline;
}
.mj-barra {
  height: 0.625rem;
  border-radius: 999px;
  background: var(--st-canvas);
  overflow: hidden;
}
.mj-barra span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--st-brand);
  transition: width 0.6s ease;
}
.mj-pin-max {
  font-weight: 700;
  color: var(--st-warning-strong);
}
.mj-pin-nota {
  display: flex;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.mj-pin-nota i {
  margin-top: 0.125rem;
}

/* Skeleton */
.mj-sk-linha {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  overflow: hidden;
}
.mj-sk-circulo,
.mj-sk-bloco {
  display: block;
  background: linear-gradient(90deg, var(--st-canvas), var(--st-border-soft), var(--st-canvas));
  background-size: 200% 100%;
  animation: mj-shimmer 1.2s ease-in-out infinite;
}
.mj-sk-circulo {
  flex: 0 0 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
}
.mj-sk-grade {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.mj-sk-bloco {
  height: 7rem;
  border-radius: var(--st-radius-lg);
}
@keyframes mj-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
@keyframes mj-brilho {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}
@media (prefers-reduced-motion: reduce) {
  .mj-atual img,
  .mj-escudo-item.mj-atual img,
  .mj-pin.mj-atual img,
  .mj-sk-circulo,
  .mj-sk-bloco {
    animation: none;
  }
  .mj-brasao img {
    transition: none;
  }
}
@media (min-width: 992px) {
  .mj-brasao {
    flex: 1 1 0;
    min-width: 5.5rem;
  }
  .mj-atual {
    flex-grow: 1.4;
  }
}
@media (max-width: 767.98px) {
  .mj-grade,
  .mj-bases,
  .mj-sk-grade {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
